/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { RecallEntryData } from '../../../Game/RecallEntry';
import AutoResizingCanvas from '../../AutoResizingCanvas';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../ComponentReference/ReferenceGroup';
import SwitcherComponentBase from '../SwitcherComponentBase';
import IPopupManager from './IPopupManager';
import { PopupCloseOption } from './PopupDispatcher';
import PopupManager from './PopupManager';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Component for displaying multiple PageView elements and menu system.
 */
@cc._decorator.ccclass
export default class PopupSwitcher extends SwitcherComponentBase<IPopupManager>
    implements IPopupManager
{
    @property({ type: InjectableComponent, visible: true })
    protected landscapePopupRef: InjectableComponent<IPopupManager>
        = new InjectableComponent(ReferenceGroup.MainGame, "PopupManager.Landscape");

    @property({ type: InjectableComponent, visible: true })
    protected portraitPopupRef: InjectableComponent<IPopupManager>
        = new InjectableComponent(ReferenceGroup.MainGame, "PopupManager.Portrait");

    private _lastPageView: cc.PageView | string = null;
    private _lastCloseOption: PopupCloseOption = PopupCloseOption.None;

    public get HasUserOpened(): boolean
    {
        return this.items.some(ref => ref.WithValue(popup => popup.HasUserOpened, false));
    }

    public get IsOpen(): boolean
    {
        return this.items.some(ref => ref.WithValue(popup => popup.IsOpen, false));
    }

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this.items.push(this.landscapePopupRef.SetContext(PopupManager));
        this.items.push(this.portraitPopupRef.SetContext(PopupManager));
    }

    protected onInitState = (): void =>
    {
        this.InjectPaytable();
    }

    protected updateDisplay(): void
    {
        this.canvasRef.WithValue(canvas =>
            {
                if (this.landscapePopupRef.WithValue(popup => popup.node.active !== canvas.IsLandScape, false)
                    || this.portraitPopupRef.WithValue(popup => popup.node.active === canvas.IsLandScape, false))
                {
                    this.Disable(false);
                    if (this._lastPageView != null)
                    {
                        this.ShowPopup(this._lastPageView, this._lastCloseOption, false);
                    }
                }
            });
    }

    public AddPage(pageViewNode: cc.Node): void
    {
        this.forEachItem(popup => popup.AddPage(pageViewNode));
    }

    public UpdateRecallEntries(recallEntryData: RecallEntryData[]): void
    {
        this.forEachItem(popup => popup.UpdateRecallEntries(recallEntryData));
    }

    public ClearRecallEntries(): void
    {
        this.forEachItem(popup => popup.ClearRecallEntries());
    }

    public Disable(clearLastPage: boolean = true): void
    {
        this.forEachItem(popup => popup.Disable());
        if (clearLastPage) { this._lastPageView = null; }
    }

    public InjectPaytable(): void
    {
        this.forEachItem(popup => popup.InjectPaytable());
    }

    public ShowPopup(
        pageView: cc.PageView | string,
        closeOption: PopupCloseOption,
        updateLastPage: boolean = true): IPopupManager
    {
        if (updateLastPage)
        {
            this._lastPageView = this._lastPageView === pageView ? null : pageView;
            this._lastCloseOption = closeOption;
        }

        this.canvasRef.WithValue(canvas =>
            {
                const popup: IPopupManager = canvas.IsLandScape
                    ? this.landscapePopupRef.Value
                    : this.portraitPopupRef.Value;

                if (!popup) { return; }
                popup.ShowPopup(pageView, closeOption);
            });

        return this;
    }
}
